import {Component, OnInit, OnDestroy} from '@angular/core';
import {Subscription} from 'rxjs';
import {CommonModule} from '@angular/common';
import {ActivatedRoute, Router} from '@angular/router';
import {ToastrService} from 'ngx-toastr';
import {OverlaySpinnerComponent} from '@shared/components/overlay-spinner/overlay-spinner.component';
import {PatientStatisticsService} from '@shared/api/services/v2/patient-statistics.service';
import {DailyBehaviourV2, DailyCheckinsV2, DailySummaryV2, isDailyNoSnapshotV2} from '@shared/api/models/query/patient-daily-snapshot.model';
import {WeeklySnapshot_Chapter} from '@shared/api/models/query/patient-weekly-snapshot.model';
import {DailyCheckinCardComponent} from '@cdss/pages/patient-overview-v2/detail/daily/components/daily-checkin-card.component';
import {DailyBehaviourPanelComponent} from '@cdss/pages/patient-overview-v2/detail/daily/components/daily-behaviour-panel.component';
import {
    DailyPatientCarerToggleComponent,
    DailyPatientCarerToggleValue
} from '@cdss/pages/patient-overview-v2/detail/daily/components/daily-patient-carer-toggle.component';
import {WeeklyChapterListComponent} from '@cdss/pages/patient-overview-v2/detail/weekly/components/weekly-chapter-list.component';
import {PatientDetailHeaderComponent} from '@cdss/pages/patient-overview-v2/components/patient-detail-header/patient-detail-header.component';
import {Patient} from '@shared/api/models/query/patient.model';
import {alignWeekStartToDate} from '@cdss/pages/patient-overview-v2/detail/daily/week-start-alignment.util';
import {emptyDailyBehaviours, emptyDailyCheckins} from '@cdss/pages/patient-overview-v2/detail/detail-no-data-placeholder';

/**
 * Patient daily summary page — v2 daily statistics contract
 * (UNIVAPLAT-2380, Task UNIVAPLAT-2360; payload contract UNIVAPLAT-2359).
 *
 * Renders exactly three sections (confirmed 2026-08-07, OQ-P1/OQ-P3 resolved):
 *   1. Daily check-in card  (patient variant)
 *   2. Chapter progress     (weekly-scoped care-programme modules — OQ-P2)
 *   3. Behaviour log        (patient label; empty array = positive "None today")
 *
 * The v1 adherence donut charts, Food Diary section, Diary-entries section and
 * risk-alert indicator were removed with the v2 redesign and must not return.
 *
 * No-data days arrive as {"data": {"data": null, "noData": true}} — the service
 * unwraps the outer envelope, so this page receives the typed DailyNoSnapshotV2
 * marker (or null) and shows the empty state. When the selected day is today,
 * the empty state reads as "check-in not completed yet" rather than "no data".
 */
@Component({
    selector: 'cdss-patient-daily-detail-page',
    standalone: true,
    imports: [
        CommonModule,
        OverlaySpinnerComponent,
        DailyCheckinCardComponent,
        DailyBehaviourPanelComponent,
        DailyPatientCarerToggleComponent,
        WeeklyChapterListComponent,
        PatientDetailHeaderComponent,
    ],
    templateUrl: './patient-daily-detail.page.html',
    styleUrl: './patient-daily-detail.page.scss'
})
export class PatientDailyDetailPage implements OnInit, OnDestroy {

    loading = true;
    patientId!: number;
    date!: string;
    weekStart: string | null = null;
    summary: DailySummaryV2 | null = null;
    noData = false;
    patient: Patient | null = null;

    private paramsSub!: Subscription;

    constructor(
        private route: ActivatedRoute,
        private router: Router,
        private patientStatisticsService: PatientStatisticsService,
        private toastr: ToastrService,
    ) {
    }

    ngOnInit(): void {
        this.patientId = Number(this.route.snapshot.params['id']);

        this.paramsSub = this.route.queryParams.subscribe(async params => {
            const date = params['date'] ?? null;

            if (!date) {
                this.toastr.error('Missing date parameter');
                this.loading = false;
                return;
            }

            // Keep weekStart aligned with the displayed date — e.g. after paging past a week
            // boundary via the day-nav arrows — so Back always resolves the week that actually
            // contains this day, and the URL stays shareable/bookmarkable (UNIVAPLAT-2360
            // follow-up). When the alignment needed correcting, re-navigate with the fixed
            // weekStart and let the resulting queryParams emission (matching this time) do the load.
            const rawWeekStart = params['weekStart'] ?? null;
            const alignedWeekStart = alignWeekStartToDate(date, rawWeekStart);
            if (alignedWeekStart !== rawWeekStart) {
                this.router.navigate([], {
                    relativeTo: this.route,
                    queryParams: {date, weekStart: alignedWeekStart},
                    replaceUrl: true,
                });
                return;
            }

            this.date = date;
            this.weekStart = alignedWeekStart;
            this.loading = true;
            this.summary = null;
            this.noData = false;
            await this.loadSummary();
        });
    }

    ngOnDestroy(): void {
        this.paramsSub?.unsubscribe();
    }

    private async loadSummary(): Promise<void> {
        try {
            const response = await this.patientStatisticsService.getDailySnapshot(
                this.patientId,
                this.date,
                'patient'
            );
            if (!response || isDailyNoSnapshotV2(response)) {
                this.noData = true;
            } else {
                this.summary = response;
            }
        } catch (error: any) {
            this.toastr.error(error?.error?.message ?? 'Failed to load daily summary');
        } finally {
            this.loading = false;
        }
    }

    /**
     * Maps the v2 `chapters` array (weekly-scoped, same source as the carer
     * view — OQ-P2) onto the shared chapter-list presentation model, unifying
     * the patient/carer daily pages onto the same component (previously the
     * patient page used a different presentational component than the carer
     * page for the same section — code-review nit, resolved here).
     */
    get chapterListItems(): WeeklySnapshot_Chapter[] {
        return (this.summary?.chapters ?? []).map(chapter => ({
            id: chapter.id,
            code: chapter.code,
            title: chapter.displayTitlePortal,
            progressValue: chapter.progressValue,
            completed: chapter.progressValue >= 1,
        }));
    }

    /** Check-ins for the card; unanswered placeholders when the day has no data (UNIVAPLAT-3259). */
    get dailyCheckins(): DailyCheckinsV2 | null {
        return this.summary?.dailyCheckins ?? (this.noData ? emptyDailyCheckins() : null);
    }

    /** Behaviours for the panel; zeroed placeholder rows when the day has no data (UNIVAPLAT-3259). */
    get behaviours(): DailyBehaviourV2[] | null {
        return this.summary?.behaviours ?? (this.noData ? emptyDailyBehaviours() : null);
    }

    get isToday(): boolean {
        const today = new Date().toISOString().split('T')[0];
        return this.date === today;
    }

    onPatientLoaded(patient: Patient): void {
        this.patient = patient;
    }

    /** Only shown when both a patient-side record and a carer record exist — otherwise
     *  there is nothing to switch to. Mirrors the tab-visibility rule already used on the
     *  Overview page (`patient?.user.phoneNumber != null`). `carer` (not `hasCarer` — the
     *  single-patient GET response does not populate that field) is the reliable signal here. */
    get showPatientCarerToggle(): boolean {
        return !!this.patient?.user?.phoneNumber && !!this.patient?.carer;
    }

    onPatientCarerToggleSelect(value: DailyPatientCarerToggleValue): void {
        if (value === 'carer') {
            this.router.navigate(
                ['/cdss/patient-overview', this.patientId, 'carer-daily'],
                {queryParams: {date: this.date, weekStart: this.weekStart}}
            );
        }
    }

    navigateToPrevDay(): void {
        const d = new Date(this.date);
        d.setDate(d.getDate() - 1);
        const prev = d.toISOString().split('T')[0];
        this.router.navigate(
            ['/cdss/patient-overview', this.patientId, 'patient-daily'],
            {queryParams: {date: prev, weekStart: this.weekStart}}
        );
    }

    navigateToNextDay(): void {
        const d = new Date(this.date);
        d.setDate(d.getDate() + 1);
        const next = d.toISOString().split('T')[0];
        this.router.navigate(
            ['/cdss/patient-overview', this.patientId, 'patient-daily'],
            {queryParams: {date: next, weekStart: this.weekStart}}
        );
    }

    /** Always returns to the Patient Overview page (not the Weekly detail sub-page), opened on
     *  the week that contains the currently displayed day (UNIVAPLAT-2360 follow-up: Back
     *  previously either went to the Weekly detail page, or — when weekStart was unknown, e.g.
     *  landing on this page via a direct/bookmarked URL with no weekStart param — dropped the
     *  day context entirely and always opened on the care programme's current week).
     *
     *  Passes `this.date` (always known — required to load this page at all), not `weekStart`
     *  (only known when it happened to arrive via the Weekly page): the Overview page resolves
     *  whichever week's [dateStart, dateEnd] range contains the given date itself, so any date
     *  inside the target week is enough — it does not need to be that week's exact start. */
    goBack(): void {
        this.router.navigate(
            ['/cdss/patient-overview', this.patientId],
            {queryParams: {type: 'patient', dateStart: this.date}}
        );
    }
}
